import { CacheKey } from "@/common/stores/persistingStores/app";
import {
	CCAttachmentMetadataInProgress,
	DisplayMessageType,
	ExtendedChatMessage,
	ExtendedCustomMessage,
	ExtendedSystemMessage,
	imageExtensions,
	MessagingMethods,
	WebchatAttachmentMetadata,
	WebchatMessage,
	WebchatMessageType,
} from "@/components/molecules/interactionWorkspace";
import { useChatStyle } from "@/components/molecules/interactionWorkspace/content/messagingInteractionContent/style";
import {
	fetchWebchatHistoryMessages,
	fetchWebchatMessages,
	sendTextMessage as sendMessage,
	WebchatHistoryMessagesKey,
	WebchatMessagesKey,
} from "@/repository/queryFns/webchatMessages";
import { useIntl } from "react-intl";
import { useInfiniteQueryHook } from "../infiniteQuery";
import { useEffect, useMemo, useState } from "react";
import { Spinner } from "@fluentui/react-components";
import {
	AttachmentMetadata,
	ChatMessage,
	CustomMessage,
	MessageProps,
	MessageRenderer,
} from "@azure/communication-react";
import dayjs from "dayjs";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { WebchatDefaultCustomerName } from "@/common/constants";
import { getFileExtensionFromName } from "@/common/helpers";
import { AttachmentCard } from "@/components/atoms/attachmentCard";
import { isValidGuid } from "@/common/helpers/validationHelper";
import {
	useWebchatCustomerClosedStore,
	WebchatCustomerClosedActions,
} from "@/common/stores/nonpersistingStores/webchatCustomerClosed";

const AnnouncementMessage = ({ text, className }: { text: string; className: string }): JSX.Element => (
	<div role="status" tabIndex={0} className={className}>
		{text}
	</div>
);

export const useWebchatMessaging = (
	agentName: string,
	interactionId: string,
	isHistory: boolean,
	addMessagesInChat: (newMessages: any) => void,
	handleError: (error: any, message: string) => void,
	imageWrapper: (
		image: JSX.Element,
		imageId: string,
		src: string
	) => JSX.Element
): MessagingMethods => {
	const messagesKey = !!isHistory
		? WebchatHistoryMessagesKey
		: WebchatMessagesKey;

	const [webchatMessagesResult, , webchatMessagesInView] =
		useInfiniteQueryHook<WebchatMessage>(
			`${messagesKey}`,
			!!isHistory ? fetchWebchatHistoryMessages : fetchWebchatMessages,
			CacheKey.Webchat,
			true,
			undefined,
			interactionId,
			"always"
		);

	const apiMessages = useMemo<WebchatMessage[]>(
		() => webchatMessagesResult.data?.pages.flatMap((w) => w.items) ?? [],
		[webchatMessagesResult, webchatMessagesInView]
	);

	const intl = useIntl();

	const defaultCustomerChatNameTranslated = intl.formatMessage({
		id: "Interaction.WebchatDefaultCustomerChatName",
	});
	const [isChatClosed, setIsChatClosed] = useState(false);

	const classes = useChatStyle();

	const isChatMessage = (
		msg: ExtendedChatMessage | ExtendedCustomMessage | ExtendedSystemMessage
	): msg is ExtendedChatMessage => {
		return (msg as ChatMessage).senderId !== undefined;
	};

	const isImageExtension = (ext: string) =>
		imageExtensions.includes(ext.toLowerCase());

	const chatMessages: (ExtendedChatMessage | CustomMessage | ExtendedSystemMessage)[] =
		useMemo(() => {
			const reversedMessages = [...apiMessages].reverse();
			const messages: (ExtendedChatMessage | CustomMessage | ExtendedSystemMessage)[] =
				reversedMessages.map((m, i) => {
					const nextMessage = reversedMessages[i - 1];
					const isDifferentSource = nextMessage?.creatorId !== m.creatorId || (!nextMessage?.creatorId && !m.creatorId);

					const isTimeGap = nextMessage
						? dayjs(m.timestamp).diff(dayjs(nextMessage.timestamp), "minutes") >
							5
						: false;
					const attached = !isDifferentSource && !isTimeGap;
					const senderId = m.creatorId || m.senderId || '';
					const mine = isValidGuid(senderId);

					if (m.type === WebchatMessageType.ParticipantJoined || m.type === WebchatMessageType.ParticipantLeft) {
						const isJoin = m.type === WebchatMessageType.ParticipantJoined;
						return {
							messageType: DisplayMessageType.System,
							createdOn: new Date(m.timestamp),
							systemMessageType: "content",
							messageId: m.id,
							iconName: isJoin ? "PeopleAdd" : "PeopleBlock",
							content: intl.formatMessage(
								{ id: isJoin ? "Interaction.Messages.WebchatParticipantJoined" : "Interaction.Messages.WebchatParticipantLeft" },
								{ name: m.body }
							),
						};
					}

					if (m.type === WebchatMessageType.Announcement) {
						return {
							messageType: DisplayMessageType.System,
							createdOn: new Date(m.timestamp),
							systemMessageType: "content",
							messageId: m.id,
							iconName: "",
							content: m.body ?? "",
							isAnnouncement: true,
						};
					}

					// Safe default: any unrecognized non-text type renders as a
					// system message rather than silently appearing as a chat bubble.
					if (m.type !== undefined && m.type !== WebchatMessageType.Text && m.type !== WebchatMessageType.Unknown) {
						return {
							messageType: DisplayMessageType.System,
							createdOn: new Date(m.timestamp),
							systemMessageType: "content",
							messageId: m.id,
							iconName: "",
							content: m.body ?? "",
						};
					}

					if (!m.body && (!m.attachments || m.attachments.length === 0)) {
						// The backend marks an inactivity close-for-customer system message with this sentinel
						// SenderName (see WebchatCustomerActionsService.InactivityCloseSystemSender). It renders NO
						// transcript line of its own (empty content, suppressed in onRenderMessage): the persisted
						// inactivity announcement already tells the agent why the thread closed, so a second
						// "...closed due to inactivity." message would duplicate it. The marker is still emitted so
						// isChatClosedMessage flags the chat closed and the composer is hidden.
						const closedDueToInactivity =
							m.senderName === "system:closed-by-inactivity";

						return {
							messageType: DisplayMessageType.System,
							createdOn: new Date(m.timestamp),
							systemMessageType: "content",
							messageId: m.id,
							iconName: "ContextMenuRemoveParticipant",
							content: closedDueToInactivity
								? ""
								: intl.formatMessage({
										id: "Interaction.Messages.WebchatCustomerClosed",
									}),
							isChatClosedMessage: true,
						};
					}

					return {
						messageType: DisplayMessageType.Chat,
						senderId: senderId,
						senderDisplayName:
							m.senderName == WebchatDefaultCustomerName
								? defaultCustomerChatNameTranslated
								: m.senderName,
						messageId: m.id,
						content: m.body,
						createdOn: new Date(m.timestamp),
						mine: mine,
						attached: attached,
						contentType: "html",
						showStatus: false,
						attachments: m.attachments
							? m.attachments.map((x) => {
									return {
										name: x.name,
										url: x.url,
										id: x.id ?? x.name,
									};
								})
							: [],
					};
				});

			messages.unshift({
				messageType: "custom",
				content: "spinner",
				messageId: "loading",
				createdOn: new Date(),
			});

			return messages;
		}, [apiMessages, intl]);

	// The transcript is the only record of the customer having left that survives a reload — the
	// notification that reports it is long gone by then — so publish it for everything outside this
	// component that has to know, such as the video call button. History transcripts carry the same
	// marker; keying by interaction id keeps that harmless.
	const markCustomerClosed = useWebchatCustomerClosedStore(
		(actions: WebchatCustomerClosedActions) => actions.markClosed
	);

	const hasChatClosedMessage = useMemo(
		() =>
			chatMessages.some(
				(message) =>
					(message as ExtendedSystemMessage).isChatClosedMessage === true
			),
		[chatMessages]
	);

	useEffect(() => {
		if (hasChatClosedMessage) {
			markCustomerClosed(interactionId);
		}
	}, [hasChatClosedMessage, interactionId, markCustomerClosed]);

	const onRenderMessage = (
		messageProps: MessageProps,
		defaultOnRender?: MessageRenderer
	): JSX.Element => {
		const { message } = messageProps as unknown as {
			message: ExtendedChatMessage | ExtendedCustomMessage | ExtendedSystemMessage;
		};

		if (message.messageType === DisplayMessageType.System) {
			const sysMsg = message;

			if (sysMsg.isChatClosedMessage) {
				setIsChatClosed(true);

				// Empty content = the inactivity close marker: it only flags the chat as closed (hiding the
				// composer) and renders no transcript line — the persisted inactivity announcement already
				// communicates why the thread closed.
				if (!sysMsg.content) {
					return <></>;
				}
			}

			if (sysMsg.isAnnouncement) {
				return <AnnouncementMessage text={sysMsg.content ?? ""} className={classes.announcementMessage} />;
			}

			return (
				<div>{defaultOnRender ? defaultOnRender(messageProps) : <></>}</div>
			);
		}

		if (!isChatMessage(message) && !!webchatMessagesResult.hasNextPage) {
			return (
				<Spinner
					ref={webchatMessagesInView.ref}
					size="tiny"
					label={intl.formatMessage({ id: "Common.Loading" })}
				/>
			);
		}

		const chatMessage = message as ChatMessage;
		const isMine = chatMessage.senderId && isValidGuid(chatMessage.senderId);
		const senderDisplayName = chatMessage.senderDisplayName;

		return (
			<div
				title={isMine ? senderDisplayName : ""}
				className={isMine ? classes.sentMessage : ""}
			>
				{defaultOnRender ? defaultOnRender(messageProps) : <></>}
				{isMine && (message as ExtendedChatMessage).showStatus && (message as ExtendedChatMessage).statusElement}
				{isMine && !(message as ExtendedChatMessage).showStatus && (
					<div style={{ width: "30px", display: "flex", flexShrink: 0 }}></div>
				)}
			</div>
		);
	};

	const sendMessages = async (
		content: string,
		attachments?: CCAttachmentMetadataInProgress[]
	) => {
		if (!content && !attachments?.length) {
			return;
		}

		const creatorId = Lockr.get(StorageKeys.UserId);
		const timestamp = new Date().toISOString();
		const id = crypto.randomUUID();

		const messageAttachmentsMetadata: WebchatAttachmentMetadata[] =
			attachments?.map((attachment) => ({
				name: attachment?.name ?? "",
				url: attachment?.url ?? "",
				id: attachment?.id ?? "",
			})) ?? [];

		const newMessage = {
			creatorId: creatorId,
			senderName: agentName,
			body: content,
			timestamp: timestamp,
			id: id,
			attachments: messageAttachmentsMetadata,
		};

		addMessagesInChat([newMessage]);

		sendMessage(interactionId, {
			message: content,
			attachments: messageAttachmentsMetadata.map((x) => ({
				name: x.name,
				url: x.url,
			})),
		}).catch((ex: any) => {
			handleError(ex, "Error while sending Webchat messages");
		});
	};

	const onRenderAttachments = (message: ExtendedChatMessage) => {
		const imageAttachments: AttachmentMetadata[] = [];
		const fileAttachments: AttachmentMetadata[] = [];

		message.attachments?.forEach((att) => {
			const ext = getFileExtensionFromName(att.name);
			if (isImageExtension(ext)) {
				imageAttachments.push(att);
			} else {
				fileAttachments.push(att);
			}
		});

		return message.attachments && message.attachments?.length > 0 ? (
			<div className={classes.webchatAttachmentsContainer}>
				{imageAttachments.length > 0 && (
					<div className={classes.webchatAttachmentSubContainer}>
						{imageAttachments.map((att) =>
							imageWrapper(
								<img
									key={att.id ?? att.name}
									src={att.url}
									alt={att.name}
									id={att.id}
									style={{
										maxWidth: "100%",
										maxHeight: "200px",
										objectFit: "contain",
										borderRadius: "4px",
										cursor: "pointer",
									}}
								/>,
								att.name,
								att.url
							)
						)}
					</div>
				)}

				{fileAttachments.length > 0 && (
					<div className={classes.webchatAttachmentSubContainer}>
						{fileAttachments.map((att) => (
							<AttachmentCard attachment={att} />
						))}
					</div>
				)}
			</div>
		) : (
			<></>
		);
	};

	return {
		chatMessages,
		apiMessages,
		onRenderMessage,
		sendMessages,
		onRenderAttachments,
		isChatClosed,
	};
};
